// WEBAPP-021: Caricamento paginato esercizi
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import usePaginatedExercises from '../../../hooks/usePaginatedExercises';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Save, Plus, Trash2, Dumbbell, AlertCircle, FileText, GripVertical, Loader2, Maximize2, Minimize2, Link2 } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NewExerciseSelector from '../../common/NewExerciseSelector';
import ImageWithPreview from '../../common/ImageWithPreview';
import ImprovedUserExerciseForm from '../../Standalone/ImprovedUserExerciseForm';
import { gymModalInput } from '../gymPageTheme';
import TemplateExerciseFields from './TemplateExerciseFields';
import useUnsavedChangesGuard from '../../../hooks/useUnsavedChangesGuard';
import { buildDefaultSeriesPlan } from '../../../constants/workoutSeriesPlan';
import { applySetTypeChangeLinks, computeWorkoutGroups, normalizeWorkoutLinks } from '../../../constants/workoutSetTypes';
import { buildTemplateRepsPayload, inferTemplateRepsMode } from '../../../constants/templateRepsMode';

const createClientRowId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? `new-${crypto.randomUUID()}`
    : `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
);

const catalogIdFromRow = (ex) => {
  if (ex.esercizio_id != null && ex.esercizio_id !== '') return Number(ex.esercizio_id);
  if (typeof ex.id === 'number') return ex.id;
  return null;
};

// Componente locale per esercizio ordinabile con drag & drop
const SortableExerciseItem = ({ exercise, index, onUpdate, onRemove, selectedExercises, group }) => {
  const rowId = exercise.id || `exercise-${index}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const catalogId = catalogIdFromRow(exercise);
  const duplicateCount = catalogId != null
    ? selectedExercises.filter((e) => catalogIdFromRow(e) === catalogId).length
    : 1;
  const ordinal = catalogId != null
    ? selectedExercises.slice(0, index + 1).filter((e) => catalogIdFromRow(e) === catalogId).length
    : 1;
  const nameSuffix = duplicateCount > 1 ? ` (${ordinal}°)` : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 rounded-lg border bg-white dark:bg-gray-900/70 border-gray-200 dark:border-white/10 ${
        group ? `border-l-4 ${group.borderClass} ${group.isFirst ? '' : 'ml-4'}` : ''
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      {group?.isFirst && (
        <div className={`-mx-4 -mt-4 mb-3 flex items-center gap-1.5 rounded-t-lg px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide ${group.badgeClass}`}>
          <Link2 className="h-3 w-3" aria-hidden />
          {group.label} · {group.size} esercizi
        </div>
      )}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <span className="text-xs font-medium text-gray-500 dark:text-white/70 bg-gray-100 dark:bg-gray-800/80 px-2 py-1 rounded">
            {index + 1}
          </span>
          {/* Immagine esercizio con preview hover */}
          {(exercise.immagine_nome || exercise.immagine_statica) ? (
            <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-800/50 flex-shrink-0">
              <ImageWithPreview
                src={exercise.immagine_nome ? `/uploads/images/${exercise.immagine_nome}` : exercise.immagine_statica}
                alt={exercise.nome}
                maxHeight="h-10"
                maxWidth="w-10"
                previewSize="w-80"
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-800/50 flex items-center justify-center flex-shrink-0">
              <span className="text-gray-400 text-xs">📷</span>
            </div>
          )}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white text-sm">
              {exercise.nome}{nameSuffix}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {exercise.gruppo_muscolare}
            </p>
            {group && (
              <span className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${group.badgeClass}`}>
                {group.label} · {group.position + 1}/{group.size}
              </span>
            )}
          </div>
        </div>
        
        <button
          onClick={() => onRemove(index)}
          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      <TemplateExerciseFields
        exercise={exercise}
        index={index}
        onUpdate={onUpdate}
        selectedExercises={selectedExercises}
        rowId={rowId}
        group={group}
      />
    </div>
  );
};

export default function CreateTemplateModal({ isOpen, onClose, onSuccess, groupId, hideHeader = false }) {
  const { getAuthenticatedApi, currentUser } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const api = getAuthenticatedApi();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    difficulty_level: 'intermediate',
    target_goal: 'general',
    estimated_duration: 45,
    notes: ''
  });

  const [selectedExercises, setSelectedExercises] = useState([]);
  const [loading, setLoading] = useState(false);

  // Esc / X / Annulla con modifiche non salvate: chiede conferma
  const { requestClose, confirmDialog } = useUnsavedChangesGuard({
    isOpen,
    value: { formData, selectedExercises },
    onClose,
  });
  const [activeTab, setActiveTab] = useState('info');
  const [validationErrors, setValidationErrors] = useState({});
  const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false); // WEBAPP-GYM-004: Stato fullscreen
  
  // State per filtri avanzati (come in archivio esercizi)
  const [filters, setFilters] = useState({
    equipment: '',
    primaryMuscle: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // WEBAPP-021: Usa hook paginato invece di caricamento completo
  const {
    exercises: availableExercises,
    loading: loadingExercises,
    initialLoading,
    hasMore,
    loadMore,
    search: searchExercises,
    filterByGroup,
    refresh: refreshExercises
  } = usePaginatedExercises({
    initialLimit: 50,
    enableInfiniteScroll: true
  });
  
  // Gestisce il cambio filtri da NewExerciseSelector
  const handleFiltersChange = useCallback((newFilters) => {
    const newEquipment = newFilters.equipment || '';
    const newPrimaryMuscle = newFilters.primaryMuscle || '';
    
    setFilters({
      equipment: newEquipment,
      primaryMuscle: newPrimaryMuscle
    });
    
    // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
    const hasFilters = newEquipment || newPrimaryMuscle;
    const hasSearch = searchTerm && searchTerm.trim().length >= 2;
    
    if (hasFilters || hasSearch) {
      searchExercises(hasSearch ? searchTerm : '', {
        equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
        primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
      });
    } else {
      // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
      searchExercises('', {});
    }
  }, [searchExercises, searchTerm]);
  
  // Gestisce la ricerca testuale
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    
    // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
    const hasSearch = term && term.trim().length >= 2;
    const hasFilters = filters.equipment || filters.primaryMuscle;
    
    if (hasSearch || hasFilters) {
      searchExercises(hasSearch ? term.trim() : '', {
        equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
        primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
      });
    }
  }, [searchExercises, filters]);
  
  // Ref per infinite scroll nel selettore esercizi
  const observerRef = useRef();
  const lastExerciseRef = useCallback(node => {
    if (loadingExercises) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loadingExercises, hasMore, loadMore]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // WEBAPP-021: L'hook gestisce già il caricamento iniziale
  // Non serve più fetchExercises manuale

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Rimuovi errore di validazione
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectExercise = (exercise) => {
    const catalogId = exercise.esercizio_id || exercise.id;
    if (!catalogId) {
      toast.error('Esercizio senza ID valido');
      return;
    }

    const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;

    setSelectedExercises(prev => [
      ...prev,
      {
        ...exercise,
        id: createClientRowId(),
        esercizio_id: catalogId,
        serie: isCardio ? 1 : 3,
        ripetizioni_max: isCardio ? 20 : 12,
        ripetizioni_min: 0,
        reps_mode: 'fixed',
        peso_suggerito: isCardio ? 0 : 0,
        tempo_recupero: 90,
        set_type: 'normal',
        linked_to_previous: false,
        order_index: prev.length,
        notes: { trainer: null, user: null, system: null },
      },
    ]);
  };

  const handleExerciseCreated = async (result) => {
    // result contiene {success: true, message: '...', exercise_id: 293}
    if (result.success && result.exercise_id) {
      try {
        // Recupera l'esercizio completo dal backend usando l'ID specifico
        const response = await api.get(`/esercizi.php?id=${result.exercise_id}`);
        
        if (response.data && response.data.id) {
          const newExercise = response.data;
          
          // WEBAPP-021: Ricarica lista esercizi per includere il nuovo
          refreshExercises();
          
          // Seleziona automaticamente l'esercizio appena creato
          handleSelectExercise(newExercise);
          
          // Chiudi il form
          setShowAddExerciseForm(false);
          
          toast.success('Esercizio creato e aggiunto al template!');
        }
      } catch (error) {
        console.error('Errore nel recupero esercizio creato:', error);
        toast.error('Esercizio creato ma errore nel recupero');
      }
    }
  };

  const updateExercise = (index, field, value) => {
    setSelectedExercises(prev => {
      const updated = prev.map((ex, i) => {
        if (i !== index) return ex;
      
        // Se set_type diventa 'normal', linked_to_previous deve essere false
        if (field === 'set_type' && value === 'normal') {
          return { ...ex, [field]: value, linked_to_previous: false, series_plan: null };
        }

        if (field === 'set_type' && value === 'piramidale') {
          const serie = parseInt(ex.serie, 10) || 3;
          const reps = parseInt(ex.ripetizioni_max, 10) || 12;
          return {
            ...ex,
            set_type: value,
            series_plan: buildDefaultSeriesPlan(serie, reps, ex.peso_suggerito),
            linked_to_previous: false,
          };
        }

        if (field === 'set_type' && value !== 'piramidale') {
          return { ...ex, [field]: value, series_plan: null };
        }
      
        return { ...ex, [field]: value };
      });
      return field === 'set_type' ? applySetTypeChangeLinks(updated, index) : normalizeWorkoutLinks(updated);
    });
  };

  const exerciseGroups = computeWorkoutGroups(selectedExercises);

  const removeExercise = (index) => {
    setSelectedExercises(prev => normalizeWorkoutLinks(prev.filter((_, i) => i !== index)));
  };

  // Gestione drag and drop
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSelectedExercises(prev => {
        const oldIndex = prev.findIndex(item => item.id === active.id);
        const newIndex = prev.findIndex(item => item.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          return normalizeWorkoutLinks(arrayMove(prev, oldIndex, newIndex));
        }
        return prev;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nome template richiesto';
    }
    
    if (selectedExercises.length === 0) {
      errors.exercises = 'Seleziona almeno un esercizio';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Validazione difficulty_level
      const validDifficulties = ['beginner', 'intermediate', 'advanced', 'expert'];
      const difficulty_level = validDifficulties.includes(formData.difficulty_level) 
        ? formData.difficulty_level 
        : 'intermediate';
      
      // Validazione target_goal
      const validGoals = ['strength', 'hypertrophy', 'endurance', 'flexibility', 'general'];
      const target_goal = validGoals.includes(formData.target_goal) 
        ? formData.target_goal 
        : 'general';

      const data = {
        name: formData.name,
        description: formData.description && formData.description.trim() !== '' ? formData.description.trim() : null,
        difficulty_level: difficulty_level,
        target_goal: target_goal,
        estimated_duration: parseInt(formData.estimated_duration) || 45,
        notes: formData.notes || null,
        is_favorite: false,
        exercises: selectedExercises.map((ex, index) => {
          const isCardio = ex.is_cardio === 1 || ex.is_cardio === true;
          
          // Un solo campo: ripetizioni_max
          // Per esercizi cardio: converti minuti in secondi per il database
          // Per esercizi normali: usa ripetizioni direttamente
          let ripetizioni_max = parseInt(ex.ripetizioni_max) || (isCardio ? 20 : 12);
          
          if (isCardio) {
            ripetizioni_max = ripetizioni_max * 60;
          }

          const repsPayload = buildTemplateRepsPayload(ex, ripetizioni_max);

          let notesValue = null;
          if (ex.notes) {
            if (typeof ex.notes === 'object' && ex.notes !== null) {
              notesValue = JSON.stringify(ex.notes);
            } else if (typeof ex.notes === 'string' && ex.notes.trim() !== '') {
              if (ex.notes.trim().startsWith('{')) {
                notesValue = ex.notes.trim();
              } else {
                notesValue = JSON.stringify({ trainer: ex.notes.trim(), user: null, system: null });
              }
            }
          }
          
          return {
            esercizio_id: catalogIdFromRow(ex),
            order_index: index,
            serie: parseInt(ex.serie) || (isCardio ? 1 : 3),
            ripetizioni_min: repsPayload.ripetizioni_min,
            ripetizioni_max: repsPayload.ripetizioni_max,
            peso_suggerito: isCardio ? 0 : (parseFloat(ex.peso_suggerito) || 0),
            tempo_recupero: (() => {
              const value = parseInt(ex.tempo_recupero);
              return isNaN(value) ? 90 : value;
            })(),
            set_type: (ex.set_type && ex.set_type.trim() !== '') ? ex.set_type.trim() : 'normal',
            series_plan: ex.set_type === 'piramidale' ? ex.series_plan : null,
            linked_to_previous: ex.linked_to_previous ? 1 : 0,
            is_isometric_scheda: ex.is_isometric_scheda ? 1 : 0, // WEBAPP-004: Campo isometrico
            notes: notesValue
          };
        })
      };


      const response = await api.post('/gym_workout_templates.php?action=create', data);

      if (response.data.success) {
        const newTemplate = response.data.template || { id: response.data.template_id };
        
        // Se è stato fornito un groupId, aggiungi automaticamente il template al gruppo
        if (groupId && newTemplate.id) {
          try {
            await api.post('/gym_template_groups.php?action=add_template', {
              group_id: groupId,
              template_id: newTemplate.id,
              display_order: 0
            });
            toast.success('Template creato e aggiunto al gruppo!');
          } catch (error) {
            console.error('Errore nell\'aggiunta al gruppo:', error);
            toast.error('Template creato ma errore nell\'aggiunta al gruppo');
          }
        } else {
          toast.success('Template creato con successo!');
        }
        
        // Passa il template creato a onSuccess per aggiornare la lista
        onSuccess(newTemplate);
      }
    } catch (error) {
      console.error('Errore nella creazione template:', error);
      toast.error('Errore nella creazione del template');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
      <div className={`${isFullscreen ? 'fixed inset-0 z-[9999] bg-white dark:bg-gray-900' : 'w-full h-full'} flex flex-col overflow-hidden`} style={!isFullscreen ? { height: 'calc(90vh - 80px)', maxHeight: 'calc(90vh - 80px)' } : {}}>
        {/* Header - Nascosto se wrapped in IOSModal */}
        {!hideHeader && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                ✨ {t('gym.membersManager.templateModal.createNewTemplate')}
              </h2>
              
              {/* WEBAPP-GYM-004: Bottoni fullscreen e chiudi */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 text-white/80 hover:text-white transition-colors"
                  title={isFullscreen ? 'Esci da schermo intero' : 'Modalità schermo intero'}
                >
                  {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                </button>
                <button
                  onClick={requestClose}
                  className="p-2 text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs e Pulsanti Azione */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
          <div className="flex items-center justify-between px-6">
            <nav className="flex space-x-4" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('info')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'info'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
                {t('gym.membersManager.templateModal.infoTab')}
              </button>
              <button
                onClick={() => setActiveTab('exercises')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'exercises'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {t('gym.membersManager.templateModal.exercisesTab')}
              </button>
              <button
                onClick={() => setActiveTab('template')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'template'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {t('gym.membersManager.templateModal.templateTab')} ({selectedExercises.length})
                {validationErrors.exercises && (
                  <AlertCircle className="h-4 w-4 text-red-500 inline ml-1" />
                )}
            </button>
            </nav>
            
            {/* Pulsanti Azione */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={requestClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('gym.membersManager.templateModal.cancel')}
              </button>
              
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {t('gym.membersManager.templateModal.createTemplate')}
                  </>
              )}
            </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-hidden flex flex-col">
          {activeTab === 'info' ? (
            <div className="space-y-4 overflow-y-auto">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('gym.membersManager.templateModal.templateName')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`${gymModalInput} rounded-lg ${
                    validationErrors.name ? 'border-red-500 dark:border-red-500' : ''
                  }`}
                  placeholder="Es: Forza Principianti"
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.name}</p>
                )}
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrizione
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className={`${gymModalInput} rounded-lg`}
                  placeholder="Programma orientato alla forza massimale per principianti..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Difficoltà */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Difficoltà
                  </label>
                  <select
                    name="difficulty_level"
                    value={formData.difficulty_level}
                    onChange={handleChange}
                    className={`${gymModalInput} rounded-lg`}
                  >
                    <option value="beginner">Principiante</option>
                    <option value="intermediate">Intermedio</option>
                    <option value="advanced">Avanzato</option>
                    <option value="expert">Esperto</option>
                  </select>
                </div>

                {/* Obiettivo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Obiettivo
                  </label>
                  <select
                    name="target_goal"
                    value={formData.target_goal}
                    onChange={handleChange}
                    className={`${gymModalInput} rounded-lg`}
                  >
                    <option value="general">Generale</option>
                    <option value="strength">Forza</option>
                    <option value="hypertrophy">Ipertrofia</option>
                    <option value="endurance">Resistenza</option>
                    <option value="flexibility">Flessibilità</option>
                  </select>
                </div>

                {/* Durata */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Durata Stimata (min)
                  </label>
                  <input
                    type="number"
                    name="estimated_duration"
                    value={formData.estimated_duration}
                    onChange={handleChange}
                    min="15"
                    max="180"
                    className={`${gymModalInput} rounded-lg`}
                  />
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Note Interne
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="2"
                  className={`${gymModalInput} rounded-lg`}
                  placeholder="Note per trainer e gestori palestra..."
                />
              </div>
            </div>
          ) : activeTab === 'template' ? (
            <div className="flex flex-col flex-1 min-h-0">
              {validationErrors.exercises && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex-shrink-0 mb-4">
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {validationErrors.exercises}
                  </p>
                </div>
              )}

              {/* Tab Template: Solo esercizi selezionati */}
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                    {t('gym.membersManager.templateModal.selectedExercises')} ({selectedExercises.length})
                    </h3>
                    {selectedExercises.length > 0 && (
                      <button
                        onClick={() => setSelectedExercises([])}
                        className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Rimuovi Tutti
                      </button>
                    )}
                  </div>
                  
                  {selectedExercises.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg min-h-0">
                      <div className="text-center">
                        <div className="text-gray-400 dark:text-gray-500 mb-2">
                          <FileText className="h-8 w-8 mx-auto" />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Nessun esercizio selezionato
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {t('gym.membersManager.templateModal.goToExercisesTab')}
                        </p>
                      </div>
                    </div>
                  ) : (
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={selectedExercises.map((ex) => ex.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="h-full overflow-y-auto space-y-3" style={{ overscrollBehavior: 'contain' }}>
                          {selectedExercises.map((exercise, index) => (
                            <SortableExerciseItem
                              key={exercise.id}
                              exercise={exercise}
                              index={index}
                              onUpdate={updateExercise}
                              onRemove={removeExercise}
                              selectedExercises={selectedExercises}
                              group={exerciseGroups[index]}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                  )}
                </div>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Tab Esercizi: Lista completa degli esercizi */}
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <div className="mb-3 flex-shrink-0">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                    {t('gym.membersManager.templateModal.selectExercises')}
                    </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('gym.membersManager.templateModal.selectExercisesDescription')}
                  </p>
                  </div>
                <div className="flex-1 min-h-0 overflow-hidden">
                    <NewExerciseSelector
                      searchTerm={searchTerm}
                      exercises={availableExercises}
                      selectedExercises={selectedExercises}
                      onSelectExercise={handleSelectExercise}
                      onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                      loading={loadingExercises || initialLoading}
                      hasMore={hasMore}
                      onLoadMore={loadMore}
                      onFiltersChange={handleFiltersChange}
                      onSearchChange={handleSearchChange}
                      gymExerciseSelector
                    />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Crea Nuovo Esercizio */}
        {showAddExerciseForm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
              <div className="flex shrink-0 items-center justify-between border-b border-[#dad8e5] bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                <h3 className="text-lg font-semibold text-[#3a3a3a] dark:font-open-sans dark:text-white">
                  {t('gym.membersManager.templateModal.createNewExercise')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowAddExerciseForm(false)}
                  className="rounded-lg p-2 text-[#3a3a3a] transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="max-h-[calc(85vh-80px)] overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                <ImprovedUserExerciseForm
                  embedded
                  onClose={() => setShowAddExerciseForm(false)}
                  onSuccess={handleExerciseCreated}
                />
              </div>
            </div>
          </div>
        )}

        {confirmDialog}
      </div>
  );
}

